import React from 'react';
import styled, { css } from 'styled-components';
import { themeGet } from 'styled-system';

import { useUpdateEffect } from 'src/hooks';

const IndicatorContext = React.createContext(null);

export function IndicatorProvider({ children }) {
  const [active, setActive] = React.useState(false);
  const value = React.useMemo(() => ({ active, setActive }), [active, setActive]);
  return <IndicatorContext.Provider value={value}>{children}</IndicatorContext.Provider>;
}

export function Indicator() {
  const { active } = React.useContext(IndicatorContext);
  const [percent, setPercent] = React.useState(0);
  const requestRef = React.useRef(null);
  const previousTimeRef = React.useRef(null);

  const animate = React.useCallback(
    time => {
      if (previousTimeRef.current) {
        const deltaTime = time - previousTimeRef.current;
        setPercent(prevPercent => prevPercent + deltaTime * 0.05);
      }
      previousTimeRef.current = time;
      requestRef.current = requestAnimationFrame(animate);
    },
    [setPercent]
  );

  React.useEffect(() => {
    if (percent >= 100) {
      cancelAnimationFrame(requestRef.current);
    }
  }, [percent]);

  React.useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useUpdateEffect(() => {
    /* if module loaded */
    if (!active) {
      setPercent(100);
    }
  }, [active]);

  return <Loader percent={percent} />;
}

export function IndicatorFallback() {
  const { setActive } = React.useContext(IndicatorContext);
  React.useEffect(() => {
    setActive(true);
    return () => setActive(false);
  });
  return null;
}

const hideStyles = css`
  opacity: 0;
  top: -10px;
  transition: width 0.5s linear, opacity 0.7s step-end, top 0.7s step-end;
`;

const Loader = styled.div<{ percent: number }>`
  position: fixed;
  opacity: 1;
  z-index: 10;
  top: 0;
  left: 0;
  height: 2px;
  width: ${props => props.percent}%;
  border-radius: 0px 1px 1px 0px;
  background-color: ${themeGet('colors.azure')};
  ${props => props.percent >= 100 && hideStyles};
`;
